import {
  BoundingBoxOptions,
  type GeographicOption,
  type GeographicTargetingState,
} from '@openx/types/targeting/geographic.ts';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState.ts';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { GeographicFormPreviewItems } from './GeographicFormPreviewItems.tsx';
import { GeographicItems } from './GeographicItems.tsx';
import { isGeoEmpty } from './utils.ts';

type GeographicReadOnlyProps = TargetingReadOnlyProps<GeographicTargetingState> & {
  onRemoveParameter?: (opt: GeographicOption) => void;
  field: TargetingFieldsValues;
  userChangedInitialValue?: boolean;
};

export const GeographicReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  onRemoveParameter,
}: GeographicReadOnlyProps) => {
  const { isDisabled, disabledReasonMessage } = useTargetingDisabledState(field);

  const isEmpty = useMemo(() => isGeoEmpty(targetingParams), [targetingParams]);
  const { t } = useTranslation();

  const hasLocation = useMemo(() => {
    const isIncludesEmpty = Object.values(targetingParams?.includes || {}).every(value => value.length === 0);
    const isExcludesEmpty = Object.values(targetingParams?.excludes || {}).every(value => value.length === 0);
    const isExcludesSubsetEmpty = targetingParams?.excludes_subset?.length === 0;

    return !isIncludesEmpty || !isExcludesEmpty || !isExcludesSubsetEmpty;
  }, [targetingParams]);

  const hasLocationRadius = useMemo(() => {
    const isCirclesEmpty = targetingParams?.circles_v2.length === 0;
    const isLocationSourceEmpty = !targetingParams?.location_source.val.size;
    const isBoundingBoxEmpty =
      !targetingParams?.bounding_box[BoundingBoxOptions.LATMIN] &&
      !targetingParams?.bounding_box[BoundingBoxOptions.LONGMIN];

    return !isCirclesEmpty || !isLocationSourceEmpty || !isBoundingBoxEmpty;
  }, [targetingParams]);

  const emptyParams: GeographicTargetingState = {
    includes: {
      continent: [],
      country: [],
      region: [],
      state: [],
      dma: [],
      msa: [],
      city: [],
      postal_code: [],
    },
    excludes: {
      continent: [],
      country: [],
      region: [],
      state: [],
      dma: [],
      msa: [],
      city: [],
      postal_code: [],
    },
    circles_v2: [],
    excludes_subset: [],
    location_source: {
      val: new Set(),
      op: Intersect.INTERSECTS,
    },
    bounding_box: {},
  };

  const optionsCount = [hasLocation, hasLocationRadius].filter(Boolean).length;
  const hasValue = optionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabled}
        description={t(
          'Define geographic targeting based on user location at varying levels of granularity, from global regions to precise local areas.'
        )}
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <GeographicFormPreviewItems
              targetingParams={targetingParams || emptyParams}
              optionsCount={optionsCount}
              hideButtons={false}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              onRemoveParameter={onRemoveParameter}
              isDisabled={isDisabled}
              disabledReasonMessage={disabledReasonMessage}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabled}
      description={t(
        'Define geographic targeting based on user location at varying levels of granularity, from global regions to precise local areas.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {!isEmpty && targetingParams && <GeographicItems targetingParams={targetingParams} />}
    </TargetingReadOnly>
  );
};
